import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { TooltipProps } from 'recharts'
import { ArrowDownRight, ArrowUpRight, ChevronLeft, ChevronRight, X } from 'lucide-react'
import {
  useAnalyticsBreakdown,
  useAnalyticsOrders,
  useAnalyticsSummary,
  type AnalyticsBreakdownRow,
  type AnalyticsBucket,
  type AnalyticsChannel,
  type AnalyticsDimension,
  type AnalyticsFreight,
  type AnalyticsMetrics,
  type AnalyticsScope,
  type AnalyticsSummary,
} from '@/api/analytics'
import { actualBarShape, BAR_RADIUS } from '@/components/forecast-bar'
import { ListFilter, ListFilters } from '@/components/list-filters'
import { ReviewChoiceGroup } from '@/components/review-choice-group'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { SearchSelect } from '@/components/search-select'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { FREIGHT_MODE_LABEL } from '@/features/purchase-orders/shipment-booking'
import {
  elapsedShare,
  FORECAST_HISTORY,
  forecastBarStyle,
  forecastRemainder,
  forecastTotal,
  naturalPeriod,
} from '@/lib/forecast'
import { cn, formatMoney, formatNumber, formatPercent, formatStatus, parseUTCDate } from '@/lib/utils'

// Series colors: categorical slots 1–3 of the validated data-viz palette, in fixed order.
const SERIES = [
  { key: 'revenue', label: 'Revenue', color: '#2a78d6' },
  { key: 'net', label: 'Net', color: '#eb6834' },
  { key: 'profit', label: 'Profit', color: '#1baf7a' },
] as const
const MARGIN_COLOR = '#1baf7a'
const FREIGHT_COLOR = '#6250d6'
const GRID_COLOR = 'oklch(0.922 0 0)'
const AXIS_TICK = { fontSize: 12, fill: 'oklch(0.556 0 0)' }

type PeriodKey = '30d' | '90d' | '6m' | 'year' | 'all'
const PERIODS: ReadonlyArray<{ value: PeriodKey; label: string }> = [
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: '6m', label: '6 months' },
  { value: 'year', label: 'This year' },
  { value: 'all', label: 'All time' },
]

const DIMENSIONS: ReadonlyArray<{ value: AnalyticsDimension; label: string; noun: string }> = [
  { value: 'manufacturer', label: 'Manufacturers', noun: 'Manufacturer' },
  { value: 'customer', label: 'Customers', noun: 'Customer' },
  { value: 'salesperson', label: 'Salespeople', noun: 'Salesperson' },
  { value: 'category', label: 'Categories', noun: 'Category' },
  { value: 'product', label: 'Products', noun: 'Product' },
]
const CHANNELS: ReadonlyArray<{ value: AnalyticsChannel; label: string }> = [
  { value: 'online', label: 'Online' },
  { value: 'dropship', label: 'Dropship' },
  { value: 'quote_builder', label: 'Quote Builder' },
]
const FREIGHTS: ReadonlyArray<AnalyticsFreight> = ['ltl', 'parcel']
const FILTER_CHOICES = 500
const LINE_DIMENSIONS: ReadonlySet<AnalyticsDimension> = new Set(['manufacturer', 'category', 'product'])
const BREAKDOWN_ROWS = 25
// Categories group one level below the department ("Cooking Equipment", not "Equipment").
const CATEGORY_LEVEL = 2

function categoryLevel(dimension: AnalyticsDimension | undefined) {
  return dimension === 'category' ? CATEGORY_LEVEL : undefined
}
const ORDERS_PER_PAGE = 20

function isoDay(date: Date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

// Analytics days are Central days for every viewer, as the server counts them (USA-1732).
const CENTRAL = 'America/Chicago'

/** Today's Central date as a local midnight, so the date arithmetic below stays plain. */
function centralToday(now = new Date()) {
  const [y = 0, m = 1, d = 1] = new Intl.DateTimeFormat('en-CA', { timeZone: CENTRAL })
    .format(now)
    .split('-')
    .map(Number)
  return new Date(y, m - 1, d)
}

function periodRange(period: PeriodKey, today = centralToday()): { start?: string; end: string } {
  const end = isoDay(today)
  const back = (days: number) => {
    const start = new Date(today)
    start.setDate(start.getDate() - days)
    return isoDay(start)
  }
  switch (period) {
    case '30d':
      return { start: back(29), end }
    case '90d':
      return { start: back(89), end }
    case '6m':
      return { start: isoDay(new Date(today.getFullYear(), today.getMonth() - 5, 1)), end }
    case 'year':
      return { start: isoDay(new Date(today.getFullYear(), 0, 1)), end }
    case 'all':
      return { end }
  }
}

function parseDay(day: string) {
  const [y = 0, m = 1, d = 1] = day.split('-').map(Number)
  return new Date(y, m - 1, d)
}

function bucketLabel(bucket: AnalyticsBucket, unit: AnalyticsSummary['bucket'], multiYear: boolean) {
  const date = parseDay(bucket.start)
  if (unit === 'month') {
    return date.toLocaleDateString('en-US', multiYear ? { month: 'short', year: '2-digit' } : { month: 'short' })
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function rangeLabel(start: string, end: string) {
  const from = parseDay(start)
  const to = parseDay(end)
  const sameYear = from.getFullYear() === to.getFullYear()
  const fromText = from.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  })
  if (start === end) return to.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  return `${fromText} – ${to.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
}

const compactMoney = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
})

function orderDate(value: string) {
  return parseUTCDate(value).toLocaleDateString('en-US', {
    timeZone: CENTRAL,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function num(value: string | number | null | undefined) {
  return value == null ? 0 : Number(value)
}

// ---------------------------------------------------------------------------
// Headline
// ---------------------------------------------------------------------------

function Change({
  current,
  previous,
  points = false,
}: {
  current: string | number | null | undefined
  previous: string | number | null | undefined
  points?: boolean
}) {
  if (current == null || previous == null) return <p className="h-4 text-xs text-muted-foreground" />
  const now = Number(current)
  const before = Number(previous)
  let text: string
  let direction: number
  if (points) {
    const delta = now - before
    direction = Math.sign(Math.round(delta * 10))
    text = `${delta > 0 ? '+' : ''}${formatNumber(delta, 1)} pts`
  } else {
    if (before === 0) return <p className="h-4 text-xs text-muted-foreground" />
    const delta = ((now - before) / Math.abs(before)) * 100
    direction = Math.sign(Math.round(delta))
    text = `${delta > 0 ? '+' : ''}${formatNumber(delta, 0)}%`
  }
  const Icon = direction > 0 ? ArrowUpRight : direction < 0 ? ArrowDownRight : null
  return (
    <p className="flex items-center gap-1 whitespace-nowrap text-xs text-muted-foreground">
      {Icon && <Icon className="h-3.5 w-3.5" aria-hidden />}
      <span className="font-medium text-foreground tabular-nums">{text}</span>
      <span>vs prior period</span>
    </p>
  )
}

function Headline({
  current,
  previous,
}: {
  current: AnalyticsMetrics
  previous: AnalyticsMetrics | null | undefined
}) {
  const cells = [
    {
      label: 'Revenue',
      value: formatMoney(current.revenue),
      change: <Change current={current.revenue} previous={previous?.revenue} />,
    },
    {
      label: 'Net',
      value: formatMoney(current.net),
      change: <Change current={current.net} previous={previous?.net} />,
    },
    {
      label: 'Profit',
      value: formatMoney(current.profit),
      negative: num(current.profit) < 0,
      change: <Change current={current.profit} previous={previous?.profit} />,
    },
    {
      label: 'Margin',
      value: formatPercent(current.margin),
      negative: num(current.margin) < 0,
      change: <Change current={current.margin} previous={previous?.margin} points />,
    },
    {
      label: 'Orders',
      value: formatNumber(current.orders, 0),
      change: (
        <p className="h-4 text-xs text-muted-foreground">
          {current.average_order != null && `${formatMoney(current.average_order)} average`}
        </p>
      ),
    },
    {
      label: 'Refunded',
      value: formatMoney(current.refunded),
      change: (
        <p className="text-xs text-muted-foreground">
          {current.cancelled_orders} cancelled · {current.refunded_orders} refunded
        </p>
      ),
    },
  ]
  return (
    <section
      aria-label="Headline figures"
      className="grid grid-cols-2 overflow-hidden rounded-[14px] border bg-card sm:grid-cols-3 xl:grid-cols-6"
    >
      {cells.map((cell) => (
        <div
          key={cell.label}
          className="space-y-1 border-b border-r px-4 py-4 last:border-r-0 sm:px-5 xl:border-b-0 [&:nth-child(2n)]:border-r-0 sm:[&:nth-child(2n)]:border-r sm:[&:nth-child(3n)]:border-r-0 xl:[&:nth-child(3n)]:border-r"
        >
          <p className="text-xs font-medium text-muted-foreground">{cell.label}</p>
          <p
            className={cn(
              'text-xl font-semibold tracking-tight tabular-nums',
              cell.negative && 'text-destructive',
            )}
          >
            {cell.value}
          </p>
          {cell.change}
        </div>
      ))}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Charts
// ---------------------------------------------------------------------------

interface ChartPoint {
  label: string
  bucket: AnalyticsBucket
  revenue: number
  net: number
  profit: number
  margin: number | null
  freight: number
  freightShare: number | null
  // The in-progress week or month only: its full-period forecast, and the part still to come.
  forecast: Record<SeriesKey, number> | null
  revenueRest?: number
  netRest?: number
  profitRest?: number
}

type SeriesKey = (typeof SERIES)[number]['key']

/**
 * Forecasts the last bucket when it is a week or month still under way, from the trend of the
 * full weeks or months before it (USA-1734). Days are too short to forecast.
 */
function withForecast(points: ChartPoint[], unit: AnalyticsSummary['bucket'], now = new Date()): ChartPoint[] {
  const current = points.at(-1)
  if (!current || unit === 'day') return points
  const isFull = (point: ChartPoint) => {
    const natural = naturalPeriod(parseDay(point.bucket.start), unit)
    const end = parseDay(point.bucket.end)
    return (
      parseDay(point.bucket.start).getTime() === natural.start.getTime() &&
      new Date(end.getFullYear(), end.getMonth(), end.getDate() + 1).getTime() === natural.end.getTime()
    )
  }
  const natural = naturalPeriod(parseDay(current.bucket.start), unit)
  const elapsed = elapsedShare(natural.start, natural.end, now)
  const history = points.slice(0, -1).filter(isFull).slice(-FORECAST_HISTORY[unit])
  const forecast = {} as Record<SeriesKey, number>
  for (const { key } of SERIES) {
    const total = forecastTotal(current[key], history.map((point) => point[key]), elapsed)
    if (total == null) return points
    forecast[key] = total
  }
  return [
    ...points.slice(0, -1),
    {
      ...current,
      forecast,
      revenueRest: forecastRemainder(current.revenue, forecast.revenue),
      netRest: forecastRemainder(current.net, forecast.net),
      profitRest: forecastRemainder(current.profit, forecast.profit),
    },
  ]
}

function ChartTooltip({ active, payload }: TooltipProps<number, string>) {
  const point = payload?.[0]?.payload as ChartPoint | undefined
  if (!active || !point) return null
  const bucket = point.bucket
  return (
    <div className="min-w-48 rounded-lg border bg-popover px-3 py-2 text-xs shadow-md">
      <p className="mb-1.5 font-medium">{rangeLabel(bucket.start, bucket.end)}</p>
      <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-0.5 tabular-nums">
        {SERIES.map((series) => (
          <div key={series.key} className="contents">
            <dt className="flex items-center gap-1.5 text-muted-foreground">
              <span className="h-2 w-2 rounded-[2px]" style={{ background: series.color }} aria-hidden />
              {series.label}
            </dt>
            <dd className="text-right">{formatMoney(bucket[series.key])}</dd>
          </div>
        ))}
        <dt className="text-muted-foreground">Margin</dt>
        <dd className="text-right">{formatPercent(bucket.margin)}</dd>
        <dt className="text-muted-foreground">Freight</dt>
        <dd className="text-right">{formatMoney(bucket.freight)}</dd>
        <dt className="text-muted-foreground">Orders</dt>
        <dd className="text-right">{bucket.orders}</dd>
        {point.forecast &&
          SERIES.map((series, index) => (
            <div key={series.key} className="contents">
              <dt className={cn('text-muted-foreground', index === 0 && 'mt-1.5 border-t pt-1.5')}>
                {series.label} forecast
              </dt>
              <dd className={cn('text-right', index === 0 && 'mt-1.5 border-t pt-1.5')}>
                {formatMoney(point.forecast?.[series.key])}
              </dd>
            </div>
          ))}
      </dl>
    </div>
  )
}

function Legend({ items, forecast = false }: { items: ReadonlyArray<{ label: string; color: string }>; forecast?: boolean }) {
  return (
    <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
      {items.map((item) => (
        <li key={item.label} className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: item.color }} aria-hidden />
          {item.label}
        </li>
      ))}
      {forecast && (
        <li className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-[3px] border border-dashed border-muted-foreground" aria-hidden />
          Forecast
        </li>
      )}
    </ul>
  )
}

function Panel({
  title,
  aside,
  children,
  className,
}: {
  title: string
  aside?: React.ReactNode
  children: React.ReactNode
  className?: string
}) {
  return (
    <section className={cn('rounded-[14px] border bg-card p-4 sm:p-5', className)} aria-label={title}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <h2 className="text-base font-semibold">{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  )
}


function TrendChart({ points, onPick }: { points: ChartPoint[]; onPick: (bucket: AnalyticsBucket) => void }) {
  return (
    <div className="h-[280px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={points}
          barGap={2}
          barCategoryGap="22%"
          margin={{ top: 4, right: 4, bottom: 0, left: 0 }}
          onClick={(state) => {
            const index = state?.activeTooltipIndex
            const point = index == null ? undefined : points[index]
            if (point) onPick(point.bucket)
          }}
          className="cursor-pointer"
        >
          <CartesianGrid vertical={false} stroke={GRID_COLOR} />
          <XAxis dataKey="label" tickLine={false} axisLine={false} tick={AXIS_TICK} minTickGap={8} />
          <YAxis
            tickLine={false}
            axisLine={false}
            tick={AXIS_TICK}
            width={56}
            tickFormatter={(value: number) => compactMoney.format(value)}
          />
          <ReferenceLine y={0} stroke="oklch(0.708 0 0)" />
          <Tooltip content={<ChartTooltip />} cursor={{ fill: 'oklch(0.97 0 0)' }} />
          {SERIES.flatMap((series) => [
            <Bar
              key={series.key}
              dataKey={series.key}
              name={series.label}
              stackId={series.key}
              fill={series.color}
              shape={actualBarShape(`${series.key}Rest`)}
              maxBarSize={22}
              isAnimationActive={false}
            />,
            <Bar
              key={`${series.key}Rest`}
              dataKey={`${series.key}Rest`}
              name={`${series.label} forecast`}
              stackId={series.key}
              {...forecastBarStyle(series.color)}
              radius={BAR_RADIUS}
              maxBarSize={22}
              isAnimationActive={false}
            />,
          ])}
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

function SmallLineChart({
  points,
  dataKey,
  color,
  format,
  onPick,
  bars = false,
}: {
  points: ChartPoint[]
  dataKey: 'margin' | 'freight'
  color: string
  format: (value: number) => string
  onPick: (bucket: AnalyticsBucket) => void
  bars?: boolean
}) {
  const shared = {
    data: points,
    margin: { top: 4, right: 4, bottom: 0, left: 0 },
    onClick: (state: { activeTooltipIndex?: number } | null) => {
      const index = state?.activeTooltipIndex
      const point = index == null ? undefined : points[index]
      if (point) onPick(point.bucket)
    },
    className: 'cursor-pointer',
  }
  const grid = <CartesianGrid vertical={false} stroke={GRID_COLOR} />
  const xAxis = <XAxis dataKey="label" tickLine={false} axisLine={false} tick={AXIS_TICK} minTickGap={8} />
  const yAxis = <YAxis tickLine={false} axisLine={false} tick={AXIS_TICK} width={56} tickFormatter={format} />
  return (
    <div className="h-[200px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        {bars ? (
          <BarChart {...shared} barCategoryGap="30%">
            {grid}
            {xAxis}
            {yAxis}
            <Tooltip content={<ChartTooltip />} cursor={{ fill: 'oklch(0.97 0 0)' }} />
            <Bar dataKey={dataKey} fill={color} radius={[4, 4, 0, 0]} maxBarSize={22} isAnimationActive={false} />
          </BarChart>
        ) : (
          <LineChart {...shared}>
            {grid}
            {xAxis}
            {yAxis}
            <Tooltip content={<ChartTooltip />} cursor={{ stroke: GRID_COLOR }} />
            <ReferenceLine y={0} stroke="oklch(0.708 0 0)" />
            <Line
              type="linear"
              dataKey={dataKey}
              stroke={color}
              strokeWidth={2}
              dot={{ r: 3, strokeWidth: 2, fill: 'white' }}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
          </LineChart>
        )}
      </ResponsiveContainer>
    </div>
  )
}

function PeriodTable({
  points,
  unit,
  onPick,
}: {
  points: ChartPoint[]
  unit: AnalyticsSummary['bucket']
  onPick: (bucket: AnalyticsBucket) => void
}) {
  const heading = unit === 'month' ? 'Month' : unit === 'week' ? 'Week' : 'Day'
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{heading}</TableHead>
          <TableHead className="text-right">Orders</TableHead>
          <TableHead className="text-right">Revenue</TableHead>
          <TableHead className="text-right">Net</TableHead>
          <TableHead className="text-right">Profit</TableHead>
          <TableHead className="text-right">Margin</TableHead>
          <TableHead className="text-right">Freight</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {[...points].reverse().map((point) => (
          <TableRow key={point.bucket.start}>
            <TableCell>
              <button
                type="button"
                onClick={() => onPick(point.bucket)}
                className="font-medium underline-offset-4 hover:underline focus-visible:underline"
              >
                {rangeLabel(point.bucket.start, point.bucket.end)}
              </button>
            </TableCell>
            <MoneyCells row={point.bucket} />
            <TableCell className="text-right tabular-nums">{formatMoney(point.bucket.freight)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

// Net is unknown when no order in the row has one yet: show a dash, not $0.
function netKnown(row: { revenue: string; margin?: string | null }) {
  return row.margin != null || num(row.revenue) === 0
}

function MoneyCells({
  row,
  leading = true,
}: {
  row: { orders: number; revenue: string; net: string; profit: string; margin?: string | null }
  leading?: boolean
}) {
  return (
    <>
      {leading && <TableCell className="text-right tabular-nums">{formatNumber(row.orders, 0)}</TableCell>}
      {leading && <TableCell className="text-right tabular-nums">{formatMoney(row.revenue)}</TableCell>}
      <TableCell className="text-right tabular-nums">{netKnown(row) ? formatMoney(row.net) : '-'}</TableCell>
      <TableCell className={cn('text-right tabular-nums', num(row.profit) < 0 && 'text-destructive')}>
        {netKnown(row) ? formatMoney(row.profit) : '-'}
      </TableCell>
      <TableCell className={cn('text-right tabular-nums', num(row.margin) < 0 && 'text-destructive')}>
        {formatPercent(row.margin)}
      </TableCell>
    </>
  )
}

// ---------------------------------------------------------------------------
// Breakdown
// ---------------------------------------------------------------------------

function EntityName({ dimension, row }: { dimension: AnalyticsDimension; row: AnalyticsBreakdownRow }) {
  const className = 'font-medium text-primary underline-offset-4 hover:underline'
  if (!row.uuid) return <span className={cn('font-medium', row.key === 'none' && 'text-muted-foreground')}>{row.label}</span>
  switch (dimension) {
    case 'manufacturer':
      return (
        <Link to="/manufacturers/$manuId" params={{ manuId: row.uuid }} className={className}>
          {row.label}
        </Link>
      )
    case 'customer':
      return (
        <Link to="/customers/$groupId" params={{ groupId: row.uuid }} className={className}>
          {row.label}
        </Link>
      )
    case 'salesperson':
      return (
        <Link to="/staff/$staffId" params={{ staffId: row.uuid }} className={className}>
          {row.label}
        </Link>
      )
    case 'product':
      return (
        <Link to="/products/$productId" params={{ productId: row.uuid }} className={className}>
          {row.label}
        </Link>
      )
    default:
      return <span className="font-medium">{row.label}</span>
  }
}

function BreakdownTable({
  dimension,
  scope,
  drill,
  emptyText,
  onPick,
}: {
  dimension: AnalyticsDimension
  scope: AnalyticsScope
  drill: Drill | null
  emptyText: string
  onPick: (row: AnalyticsBreakdownRow) => void
}) {
  const [showAll, setShowAll] = useState(false)
  const breakdown = useAnalyticsBreakdown({
    ...scope,
    dimension,
    limit: showAll ? 500 : BREAKDOWN_ROWS,
    category_level: categoryLevel(dimension),
  })
  const lineDimension = LINE_DIMENSIONS.has(dimension)
  const noun = DIMENSIONS.find((option) => option.value === dimension)?.noun ?? ''

  if (breakdown.isPending) return <Skeleton className="h-72 w-full" />
  if (breakdown.isError || !breakdown.data) {
    return <p className="py-6 text-sm text-muted-foreground">This breakdown could not load. Refresh to try again.</p>
  }
  const data = breakdown.data
  const maxRevenue = Math.max(...data.rows.map((row) => num(row.revenue)), 1)
  if (data.rows.length === 0) {
    return <p className="py-6 text-sm text-muted-foreground">{emptyText}</p>
  }

  return (
    <div className={cn('space-y-3', breakdown.isPlaceholderData && 'opacity-60 transition-opacity')}>
      <ul className="divide-y rounded-lg border bg-card md:hidden">
        {data.rows.map((row) => {
          const selected = drill?.dimension === dimension && drill.key === row.key
          return (
            <li key={row.key} className={cn('space-y-1 px-3 py-3', selected && 'bg-muted/60')}>
              <div className="flex items-baseline justify-between gap-3">
                <div className="min-w-0 truncate">
                  <EntityName dimension={dimension} row={row} />
                </div>
                <span className="shrink-0 font-medium tabular-nums">{formatMoney(row.revenue)}</span>
              </div>
              {row.detail && <p className="truncate text-xs text-muted-foreground">{row.detail}</p>}
              <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                <span className="tabular-nums">
                  {netKnown(row) ? (
                    <>
                      <span className={cn(num(row.profit) < 0 && 'text-destructive')}>
                        {formatMoney(row.profit)} Profit · {formatPercent(row.margin)}
                      </span>
                      {' · '}
                      {formatMoney(row.net)} Net
                    </>
                  ) : (
                    'Net not known yet'
                  )}
                </span>
                <button
                  type="button"
                  onClick={() => onPick(row)}
                  aria-label={`Show the ${row.orders} orders for ${row.label}`}
                  aria-pressed={selected}
                  className="shrink-0 rounded-md px-1.5 py-1 font-medium text-foreground tabular-nums underline underline-offset-4"
                >
                  {formatNumber(row.orders, 0)} order{row.orders === 1 ? '' : 's'}
                </button>
              </div>
            </li>
          )
        })}
        <li className="flex items-baseline justify-between gap-3 px-3 py-3 font-semibold">
          <span>Total</span>
          <span className="tabular-nums">{formatMoney(data.total.revenue)}</span>
        </li>
      </ul>
      <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-56">{noun}</TableHead>
              <TableHead className="text-right">Orders</TableHead>
              {lineDimension && <TableHead className="text-right">Units</TableHead>}
              <TableHead className="min-w-36 text-right">Revenue</TableHead>
              <TableHead className="text-right">Net</TableHead>
              <TableHead className="text-right">Profit</TableHead>
              <TableHead className="text-right">Margin</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.rows.map((row) => {
              const selected = drill?.dimension === dimension && drill.key === row.key
              return (
                <TableRow key={row.key} data-state={selected ? 'selected' : undefined}>
                  <TableCell className="max-w-80">
                    <div className="truncate">
                      <EntityName dimension={dimension} row={row} />
                    </div>
                    {row.detail && <p className="truncate text-xs text-muted-foreground">{row.detail}</p>}
                  </TableCell>
                  <TableCell className="text-right">
                    <button
                      type="button"
                      onClick={() => onPick(row)}
                      aria-label={`Show the ${row.orders} orders for ${row.label}`}
                      aria-pressed={selected}
                      className="rounded-md px-1.5 py-0.5 tabular-nums underline-offset-4 hover:bg-muted hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {formatNumber(row.orders, 0)}
                    </button>
                  </TableCell>
                  {lineDimension && (
                    <TableCell className="text-right tabular-nums">{formatNumber(row.units, 0)}</TableCell>
                  )}
                  <TableCell className="text-right tabular-nums">
                    <span>{formatMoney(row.revenue)}</span>
                    <span className="mt-1 ml-auto block h-1 w-full max-w-28 rounded-full bg-muted" aria-hidden>
                      <span
                        className="block h-1 rounded-full"
                        style={{
                          width: `${Math.max(0, (num(row.revenue) / maxRevenue) * 100)}%`,
                          background: SERIES[0].color,
                        }}
                      />
                    </span>
                  </TableCell>
                  <MoneyCells row={row} leading={false} />
                </TableRow>
              )
            })}
            {data.other && (
              <TableRow>
                <TableCell className="text-muted-foreground">{data.other.label}</TableCell>
                <TableCell />
                {lineDimension && (
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {formatNumber(data.other.units, 0)}
                  </TableCell>
                )}
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {formatMoney(data.other.revenue)}
                </TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">{formatMoney(data.other.net)}</TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {formatMoney(data.other.profit)}
                </TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {formatPercent(data.other.margin)}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell className="font-semibold">Total</TableCell>
              <TableCell className="text-right font-semibold tabular-nums">{formatNumber(data.total.orders, 0)}</TableCell>
              {lineDimension && <TableCell />}
              <TableCell className="text-right font-semibold tabular-nums">{formatMoney(data.total.revenue)}</TableCell>
              <TableCell className="text-right font-semibold tabular-nums">{formatMoney(data.total.net)}</TableCell>
              <TableCell className="text-right font-semibold tabular-nums">{formatMoney(data.total.profit)}</TableCell>
              <TableCell className="text-right font-semibold tabular-nums">{formatPercent(data.total.margin)}</TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
      {(data.other || showAll) && (
        <Button variant="outline" size="sm" onClick={() => setShowAll((value) => !value)}>
          {showAll ? `Show top ${BREAKDOWN_ROWS}` : 'Show all'}
        </Button>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Orders behind a figure
// ---------------------------------------------------------------------------

interface Drill {
  title: string
  share: boolean
  start?: string
  end: string
  dimension?: AnalyticsDimension
  key?: string
  source: 'trend' | 'breakdown'
}

function DrillOrders({ drill, scope, onClose }: { drill: Drill; scope: AnalyticsScope; onClose: () => void }) {
  const [page, setPage] = useState(1)
  const orders = useAnalyticsOrders(
    {
      ...scope,
      start: drill.start,
      end: drill.end,
      dimension: drill.dimension,
      key: drill.key,
      category_level: categoryLevel(drill.dimension),
      page,
      limit: ORDERS_PER_PAGE,
    },
    true,
  )
  const total = orders.data?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / ORDERS_PER_PAGE))

  return (
    <section aria-label={`Orders: ${drill.title}`} className="space-y-3 rounded-[14px] border bg-muted/30 p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">{drill.title}</h3>
          <p className="text-xs text-muted-foreground">
            {orders.data ? `${drill.share ? 'Share of ' : ''}${total} order${total === 1 ? '' : 's'}` : ' '}
          </p>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close orders">
          <X />
        </Button>
      </div>
      {orders.isPending ? (
        <Skeleton className="h-48 w-full" />
      ) : orders.isError || !orders.data ? (
        <p className="text-sm text-muted-foreground">These orders could not load. Refresh to try again.</p>
      ) : orders.data.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No orders.</p>
      ) : (
        <>
          <ul className="divide-y rounded-lg border bg-card md:hidden">
            {orders.data.items.map((order) => (
              <li key={order.uuid} className="space-y-1 px-3 py-3">
                <div className="flex items-baseline justify-between gap-3">
                  <Link
                    to="/orders/$orderId"
                    params={{ orderId: order.uuid }}
                    className="font-medium text-primary underline underline-offset-4"
                  >
                    {order.name || order.order_number}
                  </Link>
                  <span className="font-medium tabular-nums">{formatMoney(order.revenue)}</span>
                </div>
                <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                  <span className="min-w-0 truncate">
                    {orderDate(order.created_at)}
                    {order.customer_name && ` · ${order.customer_name}`}
                  </span>
                  <span className={cn('shrink-0 tabular-nums', num(order.profit) < 0 && 'text-destructive')}>
                    {formatMoney(order.profit)} · {formatPercent(order.margin)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                  <TableHead className="text-right">Net</TableHead>
                  <TableHead className="text-right">Profit</TableHead>
                  <TableHead className="text-right">Margin</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.data.items.map((order) => (
                  <TableRow key={order.uuid}>
                    <TableCell>
                      <Link
                        to="/orders/$orderId"
                        params={{ orderId: order.uuid }}
                        className="font-medium text-primary underline-offset-4 hover:underline"
                      >
                        {order.name || order.order_number}
                      </Link>
                    </TableCell>
                    <TableCell className="whitespace-nowrap tabular-nums">
                      {orderDate(order.created_at)}
                    </TableCell>
                    <TableCell className="max-w-56 truncate">
                      {order.customer_uuid ? (
                        <Link
                          to="/customers/$groupId"
                          params={{ groupId: order.customer_uuid }}
                          className="text-primary underline-offset-4 hover:underline"
                        >
                          {order.customer_name}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{formatStatus(order.status)}</Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{formatMoney(order.revenue)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatMoney(order.net)}</TableCell>
                    <TableCell className={cn('text-right tabular-nums', num(order.profit) < 0 && 'text-destructive')}>
                      {formatMoney(order.profit)}
                    </TableCell>
                    <TableCell className={cn('text-right tabular-nums', num(order.margin) < 0 && 'text-destructive')}>
                      {formatPercent(order.margin)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {pages > 1 && (
            <div className="flex items-center justify-end gap-2 text-xs text-muted-foreground">
              <span className="tabular-nums">
                Page {page} of {pages}
              </span>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page <= 1}
                onClick={() => setPage((value) => value - 1)}
                aria-label="Previous page"
              >
                <ChevronLeft />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page >= pages}
                onClick={() => setPage((value) => value + 1)}
                aria-label="Next page"
              >
                <ChevronRight />
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function AnalyticsSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-28 w-full rounded-[14px]" />
      <Skeleton className="h-[360px] w-full rounded-[14px]" />
      <div className="grid gap-6 lg:grid-cols-2">
        <Skeleton className="h-64 w-full rounded-[14px]" />
        <Skeleton className="h-64 w-full rounded-[14px]" />
      </div>
    </div>
  )
}

export function AnalyticsPage() {
  const [period, setPeriod] = useState<PeriodKey>('6m')
  const [salesPerson, setSalesPerson] = useState<string>()
  const [channel, setChannel] = useState<AnalyticsChannel>()
  const [manufacturer, setManufacturer] = useState<string>()
  const [category, setCategory] = useState<string>()
  const [freight, setFreight] = useState<AnalyticsFreight>()
  const [dimension, setDimension] = useState<AnalyticsDimension>('manufacturer')
  const [showTable, setShowTable] = useState(false)
  const [drill, setDrill] = useState<Drill | null>(null)

  const range = useMemo(() => periodRange(period), [period])
  const scope: AnalyticsScope = {
    ...range,
    sales_person: salesPerson,
    channel,
    manufacturer,
    category,
    freight,
  }
  const activeFilters = [salesPerson, channel, manufacturer, category, freight].filter(Boolean).length
  // A manufacturer, category or freight filter keeps only that share of each order.
  const keepsShare = Boolean(manufacturer || category || freight)
  const emptyText = activeFilters ? 'No orders match these filters.' : 'No orders in this period.'
  const summary = useAnalyticsSummary(scope)
  const salespeople = useAnalyticsBreakdown({ dimension: 'salesperson', limit: 100 })
  const manufacturers = useAnalyticsBreakdown({ dimension: 'manufacturer', limit: FILTER_CHOICES })
  const categories = useAnalyticsBreakdown({
    dimension: 'category',
    limit: FILTER_CHOICES,
    category_level: CATEGORY_LEVEL,
  })

  const data = summary.data
  const points: ChartPoint[] = useMemo(() => {
    if (!data) return []
    const years = new Set(data.buckets.map((bucket) => bucket.start.slice(0, 4)))
    const actual = data.buckets.map((bucket) => ({
      label: bucketLabel(bucket, data.bucket, years.size > 1),
      bucket,
      revenue: num(bucket.revenue),
      net: num(bucket.net),
      profit: num(bucket.profit),
      margin: bucket.margin == null ? null : Number(bucket.margin),
      freight: num(bucket.freight),
      freightShare: num(bucket.revenue) ? (num(bucket.freight) / num(bucket.revenue)) * 100 : null,
      forecast: null,
    }))
    return withForecast(actual, data.bucket)
  }, [data])

  function pickBucket(bucket: AnalyticsBucket) {
    setDrill({
      title: rangeLabel(bucket.start, bucket.end),
      share: keepsShare,
      start: bucket.start,
      end: bucket.end,
      source: 'trend',
    })
  }

  function pickRow(row: AnalyticsBreakdownRow) {
    setDrill({
      title: row.label,
      share: keepsShare || LINE_DIMENSIONS.has(dimension),
      start: scope.start,
      end: scope.end ?? range.end,
      dimension,
      key: row.key,
      source: 'breakdown',
    })
  }

  function changeScope(update: () => void) {
    update()
    setDrill(null)
  }

  function clearFilters() {
    changeScope(() => {
      setSalesPerson(undefined)
      setChannel(undefined)
      setManufacturer(undefined)
      setCategory(undefined)
      setFreight(undefined)
    })
  }

  const empty = !!data && data.current.orders === 0 && num(data.current.revenue) === 0 && num(data.current.net) === 0
  const freightShare = data && num(data.current.revenue) ? (num(data.current.freight) / num(data.current.revenue)) * 100 : null

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold tracking-tight">Analytics</h1>
        <div className="contents sm:flex sm:flex-wrap sm:items-center sm:gap-2">
          <ReviewChoiceGroup
            className="order-last w-full sm:order-none sm:w-auto"
            label="Period"
            value={period}
            options={PERIODS}
            onChange={(value) => changeScope(() => setPeriod(value))}
          />
          <ListFilters active={activeFilters} onClear={clearFilters}>
            <ListFilter label="Channel">
              <Select
                value={channel ?? 'all'}
                onValueChange={(value) =>
                  changeScope(() => setChannel(value === 'all' ? undefined : (value as AnalyticsChannel)))
                }
              >
                <SelectTrigger aria-label="Channel">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All channels</SelectItem>
                  {CHANNELS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </ListFilter>
            <ListFilter label="Manufacturer">
              <SearchSelect
                ariaLabel="Manufacturer"
                className="h-9"
                value={manufacturer}
                allLabel="All manufacturers"
                options={(manufacturers.data?.rows ?? []).flatMap((row) =>
                  row.uuid ? [{ value: row.uuid, label: row.label }] : [],
                )}
                onChange={(value) => changeScope(() => setManufacturer(value))}
                searchPlaceholder="Search manufacturers"
              />
            </ListFilter>
            <ListFilter label="Category">
              <SearchSelect
                ariaLabel="Category"
                className="h-9"
                value={category}
                allLabel="All categories"
                options={(categories.data?.rows ?? []).flatMap((row) =>
                  row.key === 'none' ? [] : [{ value: row.key, label: row.label, description: row.detail }],
                )}
                onChange={(value) => changeScope(() => setCategory(value))}
                searchPlaceholder="Search categories"
              />
            </ListFilter>
            <ListFilter label="Freight">
              <Select
                value={freight ?? 'all'}
                onValueChange={(value) =>
                  changeScope(() => setFreight(value === 'all' ? undefined : (value as AnalyticsFreight)))
                }
              >
                <SelectTrigger aria-label="Freight">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All freight</SelectItem>
                  {FREIGHTS.map((mode) => (
                    <SelectItem key={mode} value={mode}>
                      {FREIGHT_MODE_LABEL[mode]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </ListFilter>
            <ListFilter label="Salesperson">
              <SearchSelect
                ariaLabel="Salesperson"
                className="h-9"
                value={salesPerson}
                allLabel="All salespeople"
                options={(salespeople.data?.rows ?? []).flatMap((row) =>
                  row.uuid ? [{ value: row.uuid, label: row.label }] : [],
                )}
                onChange={(value) => changeScope(() => setSalesPerson(value))}
                searchPlaceholder="Search salespeople"
              />
            </ListFilter>
          </ListFilters>
        </div>
      </header>

      {summary.isPending ? (
        <AnalyticsSkeleton />
      ) : summary.isError || !data ? (
        <p className="rounded-[14px] border bg-card p-6 text-sm text-muted-foreground">
          Analytics could not load. Refresh to try again.
        </p>
      ) : (
        <div className={cn('space-y-6', summary.isPlaceholderData && 'opacity-60 transition-opacity')}>
          <Headline current={data.current} previous={data.previous} />

          {empty ? (
            <p className="rounded-[14px] border bg-card p-6 text-sm text-muted-foreground">{emptyText}</p>
          ) : (
            <>
              <Panel
                title="Revenue, Net and Profit"
                aside={
                  <div className="flex flex-wrap items-center gap-4">
                    <Legend items={SERIES} forecast={points.some((point) => point.forecast)} />
                    <Button variant="outline" size="sm" onClick={() => setShowTable((value) => !value)} aria-expanded={showTable}>
                      {showTable ? 'Hide table' : 'Table'}
                    </Button>
                  </div>
                }
              >
                <TrendChart points={points} onPick={pickBucket} />
                {showTable && (
                  <div className="mt-4">
                    <PeriodTable points={points} unit={data.bucket} onPick={pickBucket} />
                  </div>
                )}
              </Panel>

              <div className="grid gap-6 lg:grid-cols-2">
                <Panel
                  title="Margin"
                  aside={<span className="text-sm font-medium tabular-nums">{formatPercent(data.current.margin)}</span>}
                >
                  <SmallLineChart
                    points={points}
                    dataKey="margin"
                    color={MARGIN_COLOR}
                    format={(value) => `${formatNumber(value, 0)}%`}
                    onPick={pickBucket}
                  />
                </Panel>
                <Panel
                  title="Freight"
                  aside={
                    <span className="text-sm tabular-nums">
                      <span className="font-medium">{formatMoney(data.current.freight)}</span>
                      {freightShare != null && (
                        <span className="text-muted-foreground"> · {formatPercent(freightShare)} of Revenue</span>
                      )}
                    </span>
                  }
                >
                  <SmallLineChart
                    points={points}
                    dataKey="freight"
                    color={FREIGHT_COLOR}
                    format={(value) => compactMoney.format(value)}
                    onPick={pickBucket}
                    bars
                  />
                </Panel>
              </div>

              {drill?.source === 'trend' && (
                <DrillOrders key={JSON.stringify(drill)} drill={drill} scope={scope} onClose={() => setDrill(null)} />
              )}
            </>
          )}

          {!empty && (
            <section className="space-y-4" aria-label="Breakdown">
              <Tabs
                value={dimension}
                onValueChange={(value) => {
                  setDimension(value as AnalyticsDimension)
                  if (drill?.source === 'breakdown') setDrill(null)
                }}
              >
                <div className="overflow-x-auto">
                  <TabsList>
                    {DIMENSIONS.map((option) => (
                      <TabsTrigger key={option.value} value={option.value}>
                        {option.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </div>
              </Tabs>
              <BreakdownTable
                key={`${dimension}:${JSON.stringify(scope)}`}
                dimension={dimension}
                scope={scope}
                drill={drill}
                emptyText={emptyText}
                onPick={pickRow}
              />
              {drill?.source === 'breakdown' && (
                <DrillOrders key={JSON.stringify(drill)} drill={drill} scope={scope} onClose={() => setDrill(null)} />
              )}
            </section>
          )}
        </div>
      )}
    </div>
  )
}
